/**
 * Sum of appraised value across ALL collateral assets in a loan (bundle-aware).
 * ClickUp 86e1vg6gy — resale surfaces previously read collateral_assets[0] only,
 * under-reporting bundle appraised value and LTV.
 *
 * Per asset: prefer dynamic_appraisal_value when preferDynamic and it is a positive
 * number, else fall back to appraisal_value, else 0. Returns undefined when there are
 * no assets, so currencyAmount renders '-' rather than $0.
 */
export function sumCollateralAppraisal(
  loan: any,
  opts: { preferDynamic: boolean } = { preferDynamic: true }
): number | undefined {
  const assets = loan?.collateral_assets;
  if (!Array.isArray(assets) || assets.length === 0) {
    return undefined;
  }
  return assets.reduce((sum: number, asset: any) => {
    const dynamic = Number(asset?.dynamic_appraisal_value);
    const stat = Number(asset?.appraisal_value);
    let value = 0;
    if (opts.preferDynamic && dynamic > 0) {
      value = dynamic;
    } else if (stat > 0) {
      value = stat;
    } else if (dynamic > 0) {
      value = dynamic;
    }
    return sum + value;
  }, 0);
}

/**
 * Appraised value of a single collateral asset, in the same precedence the backend uses
 * (loan-ltv-utils.js getAppraisalValue): dynamic_appraisal_value, then appraisal_value,
 * then the 'Appraisal Value' attribute. 0 counts as unset at every step.
 */
export function getAssetAppraisal(asset: any): number {
  const dynamic = Number(asset?.dynamic_appraisal_value ?? asset?.dynamic_appraisal);
  if (isUsableAppraisal(dynamic)) return dynamic;

  const stat = Number(asset?.appraisal_value);
  if (isUsableAppraisal(stat)) return stat;

  const attribute = (asset?.attributes || []).find((attr: any) => {
    const key = String(attr?.key || '').trim().toLowerCase();
    return key === 'appraisal value' || key === 'appraisal_value';
  });
  const fromAttribute = Number(attribute?.value);
  return isUsableAppraisal(fromAttribute) ? fromAttribute : 0;
}

/**
 * Only a positive, finite appraisal can price collateral. Negative / NaN / Infinity mean the
 * data is malformed, and are treated as unset — same domain sumCollateralAppraisal uses.
 */
function isUsableAppraisal(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

/**
 * LTV (%) that `amount` represents against a loan's collateral, mirroring the backend
 * (loan-ltv-utils.js calculateLoanLtvByCollateral): amount / total appraised value, 2dp.
 *
 * Returns null in every case where the backend would return 0 — no amount, no collateral,
 * or any asset missing an appraisal — because a partial total would understate the LTV.
 * Callers should hide the figure rather than render a misleading 0%.
 */
export function calculateLtvForAmount(loan: any, amount: number): number | null {
  const normalizedAmount = Number(amount);
  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) return null;

  const assets = loan?.collateral_assets;
  if (!Array.isArray(assets) || assets.length === 0) return null;

  const appraisals = assets.map(getAssetAppraisal);
  if (appraisals.some((value) => !isUsableAppraisal(value))) return null;

  const total = appraisals.reduce((sum, value) => sum + value, 0);
  if (!isUsableAppraisal(total)) return null;

  return Math.round((normalizedAmount / total) * 100 * 100) / 100;
}
